import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useThesaurusBlock, useThesaurusChildren } from "@/hooks/api/useThesaurus";
import parse from "html-react-parser";
import { getImageUrl } from "@/utils/dedaloUtils";
import { useTreeData } from "@/contexts/treeDataContext";
import Layout from "@/components/Layout/Layout";
import Link from "@/components/LangLink/LangLink";
import Loading from "@/components/Loading/Loading";

export default function Block({ id, termId, slug }) {
    const { treeData } = useTreeData();
    const { t } = useTranslation();
    const { data, isLoading, error } = useThesaurusBlock(id);
    const { data: childrenData, isLoading: childrenLoading, error: childrenError } = useThesaurusChildren(termId);
    const [ userExpanded, setUserExpanded ] = useState(false);
    const term = data?.[0]?.term || '';
    const pageTitle = term.split(':')[0]?.trim() || '';
    const pageSubtitle = term.split(':')[1]?.trim() || '';
    const abstract = data?.[0]?.abstract || '';
    const body = data?.[0]?.body || '';
    const bodyWithHighlights = body.replaceAll('<strong>', '<p class="highlighted-p-1">').replaceAll('</strong>', '</p>');
    const imageUrl = data?.[0]?.image?.[0]?.image || '';
    const imageFootprint = data?.[0]?.image?.[0]?.footprint || '';
    const prevPath = treeData.pathOrder[treeData.pathOrder.indexOf(slug) - 1];
    const nextPath = treeData.pathOrder[treeData.pathOrder.indexOf(slug) + 1];
    const location = useLocation();

    useEffect(() => {
        setUserExpanded(false);
    }, [location.pathname])

    if (isLoading || childrenLoading) return <Layout><Loading /></Layout>
    if (error || childrenError) return <Layout><Loading error={true}/></Layout>

    return (
        <Layout>
            <div className="flow--l">
                <div className="wrapper">
                    <h1 className="is-family-secondary fluid-24-42 is-uppercase has-text-light">{pageTitle}</h1>
                    <p className="has-text-white is-size-3 is-uppercase">{pageSubtitle}</p>
                </div>
                <div className="topic-card has-background-white bottom-shadow py-7">
                    <div className="wrapper">
                            <div className="columns is-widescreen gap-8">
                                <div className="column is-relative">
                                    <div className="topic-card--image-container">
                                        <figure>
                                            <img src={getImageUrl(imageUrl)} alt=""/>
                                            <figcaption className="has-text-white is-size-4 px-6 py-3 is-flex is-justify-content-end">{imageFootprint}</figcaption>
                                        </figure>
                                    </div>
                                </div>
                                <div className="column">
                                    <div className="topic-card--content flow--m">
                                        <div className="flow--m">
                                            <div>
                                                {parse(abstract)}
                                                {body && (<>
                                                    <br/>
                                                    &nbsp;
                                                    <br/>
                                                    {userExpanded
                                                        ? (
                                                            <>{parse(bodyWithHighlights)}</>
                                                        )
                                                        : (
                                                            <button
                                                                className="button-reset is-size-5 is-uppercase has-text-weight-semibold"
                                                                onClick={() => {setUserExpanded(true);}}
                                                            >
                                                                {t('Leer mas')} <span className="is-sr-only">{`${t('sobre')} ${pageTitle}`}</span>
                                                            </button>
                                                        )
                                                    }
                                                </>)}
                                            </div>
                                            <div className="columns topic-card--links is-uppercase gap link-dn">
                                                {
                                                    childrenData.map((child) => (
                                                        <div key={child.term_id} className="column flow--2xs full-link">
                                                            <p>
                                                                <Link to={"/" + child.web_path} className="">
                                                                    <span className="has-text-weight-semibold">{child.term}</span>
                                                                </Link>
                                                            </p>
                                                            <div className="small-arrow right"></div>
                                                        </div>
                                                    ))
                                                }

                                            </div>
                                        </div>
                                        <nav className="is-flex is-justify-content-space-between">
                                            {prevPath && (
                                                <Link to={"/" + prevPath} aria-label={`${t('Ir al apartado')} ${prevPath}`} className="mr-auto">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="146" height="14"><g fill="none" fillRule="evenodd" stroke="#000"><path d="M145.44 7H1.001M7 1 1 7l6 6"/></g></svg>
                                                </Link>
                                            )}
                                            {nextPath && (
                                                <Link to={"/" + nextPath} aria-label={`${t('Ir al apartado')} ${nextPath}`} className="ml-auto">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="146" height="14"><g fill="none" fillRule="evenodd" stroke="#000000"><path d="M0 7h144.439M138.44 1l6 6-6 6"/></g></svg>
                                                </Link>
                                            )}
                                        </nav>
                                    </div>
                                </div>
                            </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}